import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useStyle } from "./styles";
import { useOutletContext } from "react-router-dom";

export interface RemovedInterval {
  start: dayjs.Dayjs | null;
  end: dayjs.Dayjs | null;
}

interface IntervaloColetaProps {
  intervalData: {
    dataInicial: dayjs.Dayjs;
    dataFinal: dayjs.Dayjs;
    observacao?: string;
    intervalosRemovidos?: RemovedInterval[];
  };
  setIntervalData: React.Dispatch<
    React.SetStateAction<{
      dataInicial: dayjs.Dayjs;
      dataFinal: dayjs.Dayjs;
      observacao?: string;
      intervalosRemovidos?: RemovedInterval[];
    }>
  >;
}
interface OutletContextType {
  config: EasyReportConfig[];
  setConfig: React.Dispatch<React.SetStateAction<EasyReportConfig[]>>;
}

export const IntervaloColeta: React.FC<IntervaloColetaProps> = ({
  intervalData,
  setIntervalData,
}) => {
  const theme = useTheme();
  const styles = useStyle(theme);
  const [timeLimit, setTimeLimit] = useState(2);
  const [errorInitialDate, setErrorInitialDate] = useState<string | null>(null);
  const [errorEndDate, setErrorEndDate] = useState<string | null>(null);

  const { config } = useOutletContext<OutletContextType>();

  useEffect(() => {
    const intervalConfig = config.find((c) => c.nome === "geral")?.configuracoes
      ?.interval;
    setTimeLimit(intervalConfig || 2);
  }, [config]);

  const isSameDate = dayjs(intervalData.dataInicial).isSame(
    dayjs(intervalData.dataFinal),
  );

  const handleAddRemoveInterval = () => {
    setIntervalData((prev) => ({
      ...prev,
      intervalosRemovidos: [
        ...(prev.intervalosRemovidos || []),
        { start: null, end: null },
      ],
    }));
  };

  const handleRemoveInterval = (index: number) => {
    setIntervalData((prev) => {
      const novosIntervalos = [...(prev.intervalosRemovidos || [])];
      novosIntervalos.splice(index, 1);
      return { ...prev, intervalosRemovidos: novosIntervalos };
    });
  };

  const handleUpdateRemoveInterval = (
    index: number,
    field: "start" | "end",
    value: dayjs.Dayjs | null,
  ) => {
    setIntervalData((prev) => {
      const novosIntervalos = [...(prev.intervalosRemovidos || [])];
      novosIntervalos[index] = { ...novosIntervalos[index], [field]: value };
      return { ...prev, intervalosRemovidos: novosIntervalos };
    });
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{"Intervalo de Coleta"}</Typography>
        <Typography sx={styles.subtitle}>
          {"Defina o período de coleta dos dados"}
        </Typography>
      </Box>
      <Divider />
      <Box sx={styles.boxIntervalo}>
        <Box sx={styles.boxInput}>
          <Typography sx={styles.titleInput}>{"Data Inicial"}</Typography>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={"pt-br"}
          >
            <DateTimePicker
              value={dayjs(intervalData.dataInicial)}
              onChange={(newValew: dayjs.Dayjs | null) => {
                if (newValew)
                  setIntervalData((prev) => ({
                    ...prev,
                    dataInicial: dayjs(newValew),
                  }));
              }}
              onError={(newError) => setErrorInitialDate(newError)}
              slotProps={{
                textField: {
                  size: "small",
                  error: !!(
                    errorEndDate === "minDate" ||
                    isSameDate ||
                    errorInitialDate
                  ),
                  helperText: errorInitialDate
                    ? `A data inicial não pode ser anterior a ${timeLimit} meses atrás`
                    : errorEndDate === "minDate"
                      ? "A Data Final não pode ser inferior à Data Inicial"
                      : isSameDate
                        ? "A data final não pode ser igual à inicial"
                        : "",
                },
              }}
              minDate={dayjs(Date.now()).subtract(timeLimit, "month")}
              maxDate={
                intervalData.dataFinal &&
                intervalData.dataFinal.isBefore(
                  dayjs(Date.now()).startOf("day"),
                )
                  ? intervalData.dataFinal
                  : dayjs(Date.now()).startOf("day")
              }
            />
          </LocalizationProvider>
        </Box>
        <Box sx={styles.boxInput}>
          <Typography sx={styles.titleInput}>{"Data Final"}</Typography>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={"pt-br"}
          >
            <DateTimePicker
              value={dayjs(intervalData.dataFinal)}
              onChange={(newValeu: dayjs.Dayjs | null) => {
                if (newValeu)
                  setIntervalData((prev) => ({
                    ...prev,
                    dataFinal: dayjs(newValeu),
                  }));
              }}
              onError={(newError) => setErrorEndDate(newError)}
              slotProps={{
                textField: {
                  size: "small",
                  helperText:
                    errorEndDate === "minDate"
                      ? "A Data Final não pode ser inferior a Data Inicial"
                      : "",
                },
              }}
              minDate={
                intervalData.dataInicial &&
                intervalData.dataInicial.isAfter(
                  dayjs(Date.now()).subtract(timeLimit, "month"),
                )
                  ? intervalData.dataInicial
                  : dayjs(Date.now()).subtract(timeLimit, "month")
              }
              maxDate={dayjs(Date.now()).startOf("day")}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Divider />

      {/* Intervalos Removidos */}
      <Box sx={styles.boxRemoveIntervals}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={styles.titleInput}>
            {"Remover Intervalos de Dados"}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleAddRemoveInterval}
          >
            Adicionar Intervalo
          </Button>
        </Box>
        <Typography sx={styles.subtitle} variant="body2">
          {
            "Selecione os períodos de data/hora (dentro do intervalo de coleta) que devem ser excluídos do relatório."
          }
        </Typography>

        {intervalData.intervalosRemovidos?.map((intervalo, index) => (
          <Box key={index} sx={styles.removeIntervalRow}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={"pt-br"}
            >
              <DateTimePicker
                label="Início da Remoção"
                value={intervalo.start ? dayjs(intervalo.start) : null}
                onChange={(newVal) =>
                  handleUpdateRemoveInterval(index, "start", newVal)
                }
                minDateTime={dayjs(intervalData.dataInicial)}
                maxDateTime={
                  intervalo.end
                    ? dayjs(intervalo.end)
                    : dayjs(intervalData.dataFinal)
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DateTimePicker
                label="Fim da Remoção"
                value={intervalo.end ? dayjs(intervalo.end) : null}
                onChange={(newVal) =>
                  handleUpdateRemoveInterval(index, "end", newVal)
                }
                minDateTime={
                  intervalo.start
                    ? dayjs(intervalo.start)
                    : dayjs(intervalData.dataInicial)
                }
                maxDateTime={dayjs(intervalData.dataFinal)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
            <IconButton
              color="error"
              aria-label="Remover intervalo"
              onClick={() => handleRemoveInterval(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Divider />
        {/* Observação */}
        <Box sx={styles.boxObservation}>
          <Typography sx={styles.titleInput}>{"Observação"}</Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            size="small"
            placeholder="Digite sua observação sobre as datas da coleta..."
            value={intervalData.observacao || ""}
            onChange={(e) =>
              setIntervalData((prev) => ({
                ...prev,
                observacao: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
    </Box>
  );
};
